/* ============================================================
   <ws-sticky-atc> — mobile-only sticky Add to Cart bar.
   - Reveals once the page is scrolled past the native Add to cart button.
   - Proxies the native <product-form> submit (real AJAX add + cart drawer).
   - Mirrors the native button's label/disabled state (Sold out / Adding...).
   - Mirrors the main product price (from the stable #Product-Price-{id}
     wrapper) so it tracks variant changes.
   - Goes inert while the cart drawer is open so it is not reachable behind it.
   Performance: trigger position is measured only at init / resize / load /
   variant-change (no forced reflow during scroll); scroll handler is
   rAF-throttled and only reads window.scrollY; price reparse is skipped while
   the bar is hidden.
   ============================================================ */
if (!customElements.get('ws-sticky-atc')) {
  customElements.define(
    'ws-sticky-atc',
    class WsStickyAtc extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('[data-ws-sticky-btn]');
        this.labelEl = this.querySelector('.ws_sticky-atc__btn-label');
        this.priceSlot = this.querySelector('[data-ws-sticky-price]');

        // The real product form + its visible Add to cart button.
        const productForm = document.querySelector('product-form');
        this.form = productForm ? productForm.querySelector('form') : null;
        if (!this.form) {
          const anyBtn = document.querySelector('form[action*="/cart/add"] [name="add"]');
          this.form = anyBtn ? anyBtn.closest('form') : null;
        }
        if (!this.form || !this.btn) return;

        const addButtons = Array.from(this.form.querySelectorAll('[name="add"]'));
        this.nativeBtn = addButtons.find((b) => b.offsetParent !== null) || addButtons[0];
        if (!this.nativeBtn) return;

        // The canonical price wrapper (#Product-Price-{sectionId}). The theme
        // swaps its innerHTML on variant change, so observe the WRAPPER (stable)
        // and re-query the inner .price each sync — never the volatile .price node.
        this.sectionId = productForm ? productForm.getAttribute('data-section-id') : null;
        this.priceWrap = this._findPriceWrap();
        this.variantInput = this.form.querySelector('[name="id"]');
        this.cartDrawer = document.querySelector('cart-drawer');
        this._pastTrigger = false;
        this._priceDirty = false;
        this._raf = null;

        this._onClick = this._onClick.bind(this);
        this._onScroll = this._onScroll.bind(this);
        this._onResize = this._onResize.bind(this);
        this._syncState = this._syncState.bind(this);
        this._syncPrice = this._syncPrice.bind(this);
        this._applyVisibility = this._applyVisibility.bind(this);

        this.btn.addEventListener('click', this._onClick);
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize);
        window.addEventListener('load', this._onResize);
        // a variant switch can change the gallery height above the button
        if (this.variantInput) this.variantInput.addEventListener('change', this._onResize);

        // mirror native button label + disabled state (incl. the .disabled class
        // the theme adds during the in-flight AJAX submit)
        this.stateMo = new MutationObserver(this._syncState);
        this.stateMo.observe(this.nativeBtn, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
        });
        this._syncState();

        // mirror price from the stable wrapper
        if (this.priceWrap) {
          this.priceMo = new MutationObserver(this._syncPrice);
          this.priceMo.observe(this.priceWrap, { childList: true, subtree: true, characterData: true });
          this._syncPrice();
        }

        // keep the bar out of the tab order / a11y tree while the drawer is open
        if (this.cartDrawer) {
          this.drawerMo = new MutationObserver(this._applyVisibility);
          this.drawerMo.observe(this.cartDrawer, { attributes: true, attributeFilter: ['class'] });
        }

        this.mq = window.matchMedia('(max-width: 749px)');
        this._computeTrigger();
        this._evaluate();
      }

      disconnectedCallback() {
        this.stateMo && this.stateMo.disconnect();
        this.priceMo && this.priceMo.disconnect();
        this.drawerMo && this.drawerMo.disconnect();
        this.btn && this.btn.removeEventListener('click', this._onClick);
        this.variantInput && this.variantInput.removeEventListener('change', this._onResize);
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('load', this._onResize);
        if (this._raf) cancelAnimationFrame(this._raf);
      }

      _findPriceWrap() {
        if (this.sectionId) {
          const byId = document.getElementById(`Product-Price-${this.sectionId}`);
          if (byId) return byId;
        }
        const wraps = Array.from(document.querySelectorAll('[id^="Product-Price-"]'));
        return wraps.find((w) => !this.contains(w)) || null;
      }

      // measure the native button's document Y (not on every scroll)
      _computeTrigger() {
        const r = this.nativeBtn.getBoundingClientRect();
        this.triggerY = window.scrollY + r.bottom;
      }

      _onResize() {
        this._computeTrigger();
        this._evaluate();
      }

      _onScroll() {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this._evaluate();
        });
      }

      _evaluate() {
        this._pastTrigger = !!(this.mq && this.mq.matches && window.scrollY > this.triggerY);
        this._applyVisibility();
      }

      _drawerOpen() {
        return !!(this.cartDrawer && this.cartDrawer.classList.contains('is-visible'));
      }

      _applyVisibility() {
        if (this._pastTrigger && !this._drawerOpen()) this._show();
        else this._hide();
      }

      _show() {
        if (this._visible) return;
        this._visible = true;
        this.classList.add('ws_sticky-atc--visible');
        this.removeAttribute('inert');
        this.setAttribute('aria-hidden', 'false');
        if (this._priceDirty) this._syncPrice();
      }

      _hide() {
        if (this._visible === false) return;
        this._visible = false;
        this.classList.remove('ws_sticky-atc--visible');
        this.setAttribute('inert', '');
        this.setAttribute('aria-hidden', 'true');
      }

      _syncState() {
        const disabled =
          this.nativeBtn.disabled ||
          this.nativeBtn.getAttribute('aria-disabled') === 'true' ||
          this.nativeBtn.classList.contains('disabled');
        this.btn.disabled = disabled;
        const label = (this.nativeBtn.textContent || '').trim();
        if (label && this.labelEl) this.labelEl.textContent = label;
      }

      _syncPrice() {
        if (!this.priceWrap || !this.priceSlot) return;
        // avoid a serialize/reparse behind an offscreen bar — defer to _show()
        if (this._visible === false) {
          this._priceDirty = true;
          return;
        }
        this._priceDirty = false;
        const price = this.priceWrap.querySelector('.price');
        if (!price) return;
        this.priceSlot.innerHTML = price.outerHTML;
        this.priceSlot.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
      }

      _onClick() {
        if (this.btn.disabled) return;
        // immediate guard against a double-tap landing two /cart/add calls
        this.btn.disabled = true;
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit(this.nativeBtn);
        } else {
          this.nativeBtn.click();
        }
      }
    }
  );
}
